import { useEffect, useState } from "react";
import { useNetworkStatus } from "../../Hooks/useNetworkStatus";

// Single reusable shimmer block
function Shimmer({ className = "" }: { className?: string }) {
  return (
    <div
      className={`animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 ${className}`}
    />
  );
}

// Product card skeleton
function ProductCardSkeleton() {
  return (
    <div className="rounded-xl overflow-hidden bg-white dark:bg-dark-foreground shadow-sm p-3 flex flex-col gap-2">
      <Shimmer className="h-36 w-full rounded-lg" />
      <Shimmer className="h-4 w-3/4" />
      <Shimmer className="h-4 w-1/2" />
      <Shimmer className="h-8 w-full rounded-full mt-1" />
    </div>
  );
}

// Full page skeleton that mimics the main layout
export function PageSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-background px-4 pt-4 pb-10">
      {/* Banner strip */}
      <Shimmer className="h-44 sm:h-56 w-full mb-6 rounded-2xl" />

      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <Shimmer className="h-5 w-36" />
        <Shimmer className="h-4 w-16" />
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-8">
        {Array.from({ length: 8 }).map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>

      {/* Second section */}
      <div className="flex items-center justify-between mb-4">
        <Shimmer className="h-5 w-44" />
        <Shimmer className="h-4 w-16" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

// Floating toast shown after N seconds of loading on a slow connection
export function SlowNetworkOverlay() {
  const { speed, isOnline } = useNetworkStatus();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (speed === "slow" && isOnline) {
      // Show after 4 seconds on slow connection
      const show = setTimeout(() => setVisible(true), 4000);
      // Auto-hide after 6 more seconds
      const hide = setTimeout(() => setVisible(false), 10000);
      return () => {
        clearTimeout(show);
        clearTimeout(hide);
      };
    }
    setVisible(false);
  }, [speed, isOnline]);

  if (!visible) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="
        fixed bottom-6 left-1/2 -translate-x-1/2 z-50
        flex items-center gap-3
        bg-white dark:bg-dark-foreground
        border border-gray-100 dark:border-dark-foreground
        shadow-lg rounded-2xl px-5 py-3
        pointer-events-none
      "
    >
      <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse flex-shrink-0" />
      <span className="text-sm text-gray-600 dark:text-gray-300 whitespace-nowrap">
        Slow connection — still loading&hellip;
      </span>
    </div>
  );
}
